import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, Play, RefreshCcw, CalendarClock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { computeSplits, pct } from "@/lib/splits";

const money = (c) => `$${(Number(c || 0) / 100).toFixed(2)}`;
const etTime = (iso) => `${new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET`;
const STATUS_CLS = { paid: "text-emerald-400", failed: "text-[var(--ff-red)]", skipped: "text-yellow-300", completed: "text-emerald-400", running: "text-sky-300" };

function SplitRules() {
  return (
    <div className="surface-card p-5" data-testid="payout-split-rules">
      <div className="label-eyebrow mb-3">▮ SPLIT RULES</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--ff-border)] text-sm">
        <div className="bg-[var(--ff-surface)] p-3"><div className="font-display">Solo host</div><div className="text-[var(--ff-text-dim)] text-xs">Host 90% · Network 10%</div></div>
        <div className="bg-[var(--ff-surface)] p-3"><div className="font-display">Co-hosts (2+)</div><div className="text-[var(--ff-text-dim)] text-xs">Network 30% first · Showrunner +10% · rest split evenly</div></div>
        <div className="bg-[var(--ff-surface)] p-3"><div className="font-display">Founder no-fee shows</div><div className="text-[var(--ff-text-dim)] text-xs">No network fee · 100% split evenly (50/50)</div></div>
        <div className="bg-[var(--ff-surface)] p-3"><div className="font-display">Donations</div><div className="text-[var(--ff-text-dim)] text-xs">Receiving host 90% · Network 10%</div></div>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 font-mono-ui text-[0.65rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">
        {[2, 3, 4, 5, 6].map((n) => { const s = computeSplits(n); return <span key={n}>{n} hosts: ★{pct(s.showrunner)} / {pct(s.other)} each</span>; })}
      </div>
      <p className="text-xs text-[var(--ff-text-dim)] mt-2">Applies to show memberships and show merch (item price × qty, excluding shipping &amp; tax). Earnings recorded before this change keep their original split.</p>
    </div>
  );
}

function PendingTable({ pending }) {
  if (!pending.length) return <div className="surface-card p-6 text-sm text-[var(--ff-text-dim)]" data-testid="payout-pending-empty">Nothing pending — every host is paid up.</div>;
  return (
    <div className="surface-card divide-y divide-[var(--ff-border)]" data-testid="payout-pending-table">
      {pending.map((p) => (
        <div key={`${p.host_id}-${p.provider}`} className="p-3 flex flex-wrap items-center gap-4 text-sm" data-testid={`payout-pending-${p.host_id}-${p.provider}`}>
          <div className="flex-1 min-w-[160px] font-display">{p.host_name}</div>
          <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">{p.provider} · {p.rows} item{p.rows === 1 ? "" : "s"}</span>
          {p.destination_ok
            ? <span className="text-emerald-400 text-xs inline-flex items-center gap-1"><CheckCircle2 size={12} /> Ready</span>
            : <span className="text-yellow-300 text-xs inline-flex items-center gap-1"><AlertTriangle size={12} /> {p.provider === "stripe" ? "No Stripe account" : "No PayPal email"}</span>}
          <span className="font-mono-ui w-24 text-right">{money(p.amount_cents)}</span>
        </div>
      ))}
    </div>
  );
}

function NeedsReview({ groups, onDone }) {
  const act = async (g, action) => {
    const msg = action === "paid" ? `Confirm ${g.host_name} already received ${money(g.amount_cents)} via ${g.provider}?` : `Return ${money(g.amount_cents)} for ${g.host_name} to pending? It will be sent on the next run.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post("/admin/payouts/review", { host_id: g.host_id, provider: g.provider, action });
      toast.success(action === "paid" ? "Marked as paid" : "Returned to pending");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="border border-[var(--ff-red)]/50 bg-[var(--ff-red)]/5 p-4" data-testid="payout-needs-review">
      <div className="label-eyebrow text-[var(--ff-red)] mb-1">▮ NEEDS REVIEW</div>
      <p className="text-xs text-[var(--ff-text-dim)] mb-3">A payout run was interrupted while sending these. Check your Stripe / PayPal dashboard: if the host got the money, mark it paid — otherwise return it to pending. They are never re-sent automatically.</p>
      {groups.map((g) => (
        <div key={`${g.host_id}-${g.provider}`} className="flex flex-wrap items-center gap-3 py-2 border-t border-[var(--ff-border)] text-sm" data-testid={`payout-review-${g.host_id}-${g.provider}`}>
          <span className="flex-1 font-display">{g.host_name}</span>
          <span className="font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]">{g.provider}</span>
          <span className="font-mono-ui">{money(g.amount_cents)}</span>
          <button onClick={() => act(g, "paid")} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid={`payout-review-paid-${g.host_id}-${g.provider}`}><CheckCircle2 size={12} /> Mark Paid</button>
          <button onClick={() => act(g, "release")} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid={`payout-review-release-${g.host_id}-${g.provider}`}><RefreshCcw size={12} /> Return to Pending</button>
        </div>
      ))}
    </div>
  );
}

function RunsList({ runs }) {
  if (!runs.length) return <div className="text-sm text-[var(--ff-text-dim)]">No payout runs yet.</div>;
  return (
    <div className="space-y-2" data-testid="payout-runs">
      {runs.map((r) => (
        <details key={r.id} className="surface-card p-3" data-testid={`payout-run-${r.id}`}>
          <summary className="cursor-pointer flex flex-wrap items-center gap-3 text-sm">
            <span className={`font-mono-ui text-[0.65rem] uppercase tracking-[0.15em] ${STATUS_CLS[r.status] || ""}`}>{r.status}</span>
            <span>{new Date(r.started_at).toLocaleString()}</span>
            <span className="text-[var(--ff-text-dim)] text-xs">{r.triggered_by}</span>
            {r.totals && <span className="ml-auto font-mono-ui text-xs">paid {money(r.totals.paid)} · failed {money(r.totals.failed)} · skipped {money(r.totals.skipped)}</span>}
          </summary>
          {r.error && <div className="text-xs text-yellow-300 mt-2">{r.error}</div>}
          <ul className="mt-2 space-y-1">
            {(r.results || []).map((x, i) => (
              <li key={i} className="text-xs flex flex-wrap gap-3">
                <span className={STATUS_CLS[x.status]}>{x.status}</span><span>{x.host_name}</span><span className="text-[var(--ff-text-dim)]">{x.provider}</span>
                <span className="font-mono-ui">{money(x.amount_cents)}</span>{x.error && <span className="text-[var(--ff-text-dim)]">— {x.error}</span>}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

export default function PayoutsAdmin() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/payouts").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    try {
      await api.put("/admin/site-settings", { weekly_payouts_enabled: !d.enabled });
      toast.success(!d.enabled ? "Automatic weekly payouts ON" : "Automatic weekly payouts paused");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runNow = async () => {
    if (!window.confirm(`Send REAL payouts now? ${money(d.pending_total_cents)} pending goes to every host with a linked Stripe account / PayPal email.`)) return;
    setBusy(true);
    try {
      await api.post("/admin/payouts/run");
      toast.success("Payout run started");
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!d) return <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="payouts-admin">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-1">▮ HOST PAYOUTS</div>
          <h2 className="font-display text-3xl">Weekly Payouts</h2>
          <p className="text-sm text-[var(--ff-text-dim)] mt-1 inline-flex items-center gap-1.5" data-testid="payout-next-run"><CalendarClock size={13} /> {d.schedule} · next run {etTime(d.next_run_at)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={toggle} className={`${d.enabled ? "btn-primary" : "btn-ghost inline-flex items-center gap-1.5"} text-xs`} data-testid="payout-toggle-btn">
            <Banknote size={12} /> {d.enabled ? "Auto Payouts: ON" : "Auto Payouts: OFF"}
          </button>
          <button onClick={runNow} disabled={busy || !d.pending.length} className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-40" data-testid="payout-run-now-btn">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run Payouts Now
          </button>
          <button onClick={load} className="btn-ghost inline-flex items-center gap-1.5 text-xs" data-testid="payout-refresh-btn"><RefreshCcw size={12} /></button>
        </div>
      </div>
      {!d.enabled && <div className="border border-yellow-300/40 bg-yellow-300/5 p-3 text-sm" data-testid="payout-disabled-note">Automatic Friday payouts are <b>OFF</b>. Earnings keep accumulating as pending until you turn them on or run them manually.</div>}
      {d.needs_review?.length > 0 && <NeedsReview groups={d.needs_review} onDone={load} />}
      <SplitRules />
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow">▮ PENDING THIS WEEK</div>
          <div className="font-display text-xl" data-testid="payout-pending-total">{money(d.pending_total_cents)}</div>
        </div>
        <PendingTable pending={d.pending} />
      </div>
      <div>
        <div className="label-eyebrow mb-2">▮ RECENT RUNS</div>
        <RunsList runs={d.runs} />
      </div>
    </div>
  );
}
